import {
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";
import CustomCloseButton from "components/CustomCloseButton";
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { format, isPast, isThisYear } from "date-fns";
import { useNavigate } from "react-router";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import { ReminderRow } from "../types";
import ReminderRowItem from "./ReminderRow";
import ReminderComposer from "./ReminderComposer";

interface NewHireInfo {
  fullName: string;
  avatarUrl?: string;
  title?: string;
  startDate?: Date | string;
}

interface SendRemindersQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
  rows: ReminderRow[];
  youRow?: ReminderRow;
  newHireJourneyId: string;
  newHire?: NewHireInfo;
}

type View = { kind: "list" } | { kind: "composer"; userId: string };

export default function SendRemindersQueueModal({
  isOpen,
  onClose,
  rows,
  youRow,
  newHireJourneyId,
  newHire,
}: SendRemindersQueueModalProps) {
  const navigate = useNavigate();
  const [view, setView] = useState<View>({ kind: "list" });
  const actionButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const returnToButtonId = useRef<string | undefined>(undefined);

  const handleClose = useCallback(() => {
    setView({ kind: "list" });
    returnToButtonId.current = undefined;
    onClose();
  }, [onClose]);

  const handleAction = useCallback((row: ReminderRow) => {
    if (row.isSelf) {
      handleClose();
      navigate(`${SHARED_ROUTES.myTasks}?newHireId=${newHireJourneyId}`);
      return;
    }
    returnToButtonId.current = row.user.id;
    setView({ kind: "composer", userId: row.user.id });
  }, [navigate, newHireJourneyId, handleClose]);

  const handleBack = useCallback(() => {
    setView({ kind: "list" });
  }, []);

  useEffect(() => {
    if (view.kind === "list" && returnToButtonId.current) {
      const button = actionButtonRefs.current.get(returnToButtonId.current);
      button?.focus();
    }
  }, [view]);

  const selectedRow = useMemo(() => {
    if (view.kind !== "composer") return undefined;
    return rows.find((r) => r.user.id === view.userId);
  }, [view, rows]);

  const registerButton = useCallback((userId: string, button: HTMLButtonElement | null) => {
    if (button) {
      actionButtonRefs.current.set(userId, button);
    } else {
      actionButtonRefs.current.delete(userId);
    }
  }, []);

  const newHireName = newHire?.fullName;
  const isEmpty = rows.length === 0 && !youRow;

  const startDate = newHire?.startDate;
  const startDateLabel = useMemo(() => {
    if (!startDate) return undefined;
    const date = new Date(startDate);
    const dateStr = format(date, isThisYear(date) ? "MMM d" : "MMM d, yyyy");
    return isPast(date) ? `Started on ${dateStr}` : `Starts on ${dateStr}`;
  }, [startDate]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="640px">
        {view.kind === "list" ? (
          <>
            <ModalHeader textAlign="center" pb={1} pt={6}>
              <Text fontSize="xl" fontWeight="bold">Send reminders</Text>
              {newHireName && (
                <Text fontSize="sm" color="fg.muted" fontWeight="normal" mt={1}>
                  {newHireName}
                  {" · Onboarding"}
                  {startDateLabel && ` · ${startDateLabel}`}
                </Text>
              )}
            </ModalHeader>
            <CustomCloseButton onClick={handleClose} top="2.5" />
            <ModalBody pb={6} pt={2}>
              {isEmpty ? (
                <Text textAlign="center" color="fg.muted" py={8}>
                  {newHireName
                    ? `Everyone's caught up — no open tasks for ${newHireName} right now.`
                    : "Everyone's caught up — no open tasks right now."}
                </Text>
              ) : (
                <VStack align="stretch" spacing={0} divider={<Divider />}>
                  {rows.map((row) => (
                    <ReminderRowItem
                      key={row.user.id}
                      row={row}
                      onAction={handleAction}
                      ref={(button) => registerButton(row.user.id, button)}
                    />
                  ))}
                  {youRow && (
                    <ReminderRowItem
                      key={youRow.user.id}
                      row={youRow}
                      onAction={handleAction}
                      ref={(button) => registerButton(youRow.user.id, button)}
                    />
                  )}
                </VStack>
              )}
            </ModalBody>
          </>
        ) : (
          <>
            <CustomCloseButton onClick={handleClose} top="2.5" />
            <ModalBody pt={6} pb={6}>
              {selectedRow ? (
                <ReminderComposer
                  row={selectedRow}
                  newHireFullName={newHireName}
                  newHireId={newHire?.id}
                  onBack={handleBack}
                  onSent={handleBack}
                />
              ) : (
                <VStack spacing={4} align="start">
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<Icon as={FiArrowLeft} />}
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                  </HStack>
                  <Box w="full" textAlign="center" py={8}>
                    <Text color="fg.muted">This person no longer has open tasks.</Text>
                  </Box>
                </VStack>
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
